'use client'

import React, { useCallback, useEffect, useRef, useState } from 'react'
// import { ChevronDownIcon } from '@heroicons/react/20/solid' // Removed
import ChevronDownIcon from '../../icons/ChevronDownIcon' // Added local import
import { SortMode, SortOrder } from './AppSearchToolbar'
import CustomRadioButton from './CustomRadioButton' // Import the new component

// Mapping for display labels
const sortModeLabels: Record<SortMode | 'Select', string> = {
  Select: 'Select sort', // Placeholder if none is active
  Default: 'Default', // Keeping Default for logic, might not be shown if not in available modes
  Alphabetical: 'Alphabetical',
  'Last Edited': 'Last Modified', // Map internal value to display label
  Created: 'Date Created', // Map internal value to display label
}

const sortOrderLabels: Record<SortOrder, string> = {
  Ascending: 'Oldest First',
  Descending: 'Newest First',
}

// Helper function to get the display label for sort order
const getDisplaySortOrderLabel = (
  order: SortOrder,
  activeSortMode: SortMode | undefined,
  baseLabels: Record<SortOrder, string>
): string => {
  if (activeSortMode === 'Alphabetical') {
    return order === 'Ascending' ? 'A-Z' : 'Z-A'
  }
  // For other modes like 'Last Edited', 'Created', or fallback, use base labels.
  // The UI visibility for 'Default' mode's order section is handled separately.
  return baseLabels[order]
}

interface SortOptionsProps {
  availableSortModes: SortMode[]
  activeSortMode?: SortMode
  activeSortOrder?: SortOrder | null
  onSortModeChange?: (mode: SortMode) => void
  onSortOrderChange?: (order: SortOrder) => void
  className?: string
}

const SortOptions = ({
  availableSortModes = [],
  activeSortMode = 'Default',
  activeSortOrder = 'Descending', // Defaulting order for display consistency
  onSortModeChange,
  onSortOrderChange,
  className = '',
}: SortOptionsProps): React.ReactElement => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Get the display label for the active mode, handle potential undefined activeSortMode
  const activeModeLabel = activeSortMode
    ? (sortModeLabels[activeSortMode] ?? 'Select sort')
    : 'Select sort'

  const handleSortModeClick = useCallback(
    (mode: SortMode) => {
      onSortModeChange?.(mode)

      // Set default sort order based on the selected mode if onSortOrderChange is provided
      if (onSortOrderChange) {
        if (mode === 'Alphabetical') {
          onSortOrderChange('Ascending')
        } else if (mode === 'Last Edited' || mode === 'Created') {
          onSortOrderChange('Descending')
        }
        // For 'Default' mode or other modes, the sort order is not automatically changed here.
        // It will retain its current value. The UI for order selection will be hidden for 'Default'.
      }

      // Close dropdown only if there's no capability to change order at all.
      // If onSortOrderChange is available, the dropdown stays open, allowing users to pick an order
      // for non-Default modes.
      if (!onSortOrderChange) {
        setIsOpen(false)
      }
    },
    [onSortModeChange, onSortOrderChange, setIsOpen]
  )

  const handleSortOrderClick = useCallback(
    (order: SortOrder) => {
      // Only allow changing order if a mode that supports ordering is active
      // and the callback is provided.
      if (onSortOrderChange && activeSortMode !== 'Default') {
        onSortOrderChange(order)
      }
      // Keep the dropdown open after selecting an order.
    },
    [onSortOrderChange, activeSortMode]
  )

  // Close dropdown if clicked outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [dropdownRef, setIsOpen])

  return (
    <div className={`relative inline-block text-left ${className}`} ref={dropdownRef}>
      {/* Dropdown Trigger Button */}
      <div>
        <button
          type="button"
          className="font-space-grotesk inline-flex h-[40px] w-full min-w-[180px] items-center justify-center gap-x-2 truncate rounded-2xl border border-black/10 bg-white/80 px-4 text-sm font-medium text-gray-900 shadow-sm backdrop-blur-xl transition hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-300"
          id="menu-button"
          aria-expanded={isOpen}
          aria-haspopup="true"
          onClick={() => setIsOpen(!isOpen)}
        >
          Sort: {activeModeLabel}
          <ChevronDownIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
        </button>
      </div>

      {/* Dropdown Panel */}
      {isOpen && (
        <div
          className="absolute right-0 z-50 mt-2 w-56 origin-top-right rounded-2xl border border-black/10 bg-white/80 px-4 py-3 shadow-sm backdrop-blur-xl focus:outline-none"
          role="menu"
          aria-orientation="vertical"
          aria-labelledby="menu-button"
        >
          <div className="py-1" role="none">
            {/* Sort By Section */}
            <div className="px-4 py-2">
              <p className="font-space-grotesk text-sm font-medium text-gray-500">Sort by</p>
              {availableSortModes.map((mode) => (
                <CustomRadioButton
                  key={mode}
                  id={`sort-mode-${mode}`}
                  name="sort-mode"
                  checked={activeSortMode === mode}
                  onChange={() => handleSortModeClick(mode)}
                  label={sortModeLabels[mode]}
                  className="font-space-grotesk mt-2 block"
                />
              ))}
            </div>

            {/* Separator */}
            {/* Apply transition classes and conditional opacity/margin/height */}
            <hr
              className={`border-gray-200 transition-all duration-300 ease-in-out ${
                activeSortMode !== 'Default' && onSortOrderChange
                  ? 'my-1 opacity-100' // Visible state
                  : 'my-0 h-0 border-0 opacity-0' // Hidden state - hide border, height, and margin
              }`}
            />

            {/* Order Section - Apply transition classes and conditional opacity/height/padding */}
            {/* Keep the div rendered, control visibility with classes */}
            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                activeSortMode !== 'Default' && onSortOrderChange
                  ? 'max-h-40 px-4 py-2 opacity-100' // Visible state with padding
                  : 'max-h-0 p-0 opacity-0' // Hidden state, remove padding
              }`}
            >
              <p className="font-space-grotesk text-sm font-medium text-gray-500">Order</p>
              {(Object.keys(sortOrderLabels) as SortOrder[]).map((order) => {
                // Determine the label based on the active sort mode using the helper function
                const label = getDisplaySortOrderLabel(order, activeSortMode, sortOrderLabels)

                return (
                  <CustomRadioButton
                    key={order}
                    id={`sort-order-${order}`}
                    name="sort-order"
                    checked={activeSortOrder === order}
                    onChange={() => handleSortOrderClick(order)}
                    label={label}
                    className="font-space-grotesk mt-2 block"
                  />
                )
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SortOptions
